/**
 * Adapter-neutral typed layout descriptors (ADR-UI-001, ADR-015).
 *
 * NOTHING in this module references React or any layout library. These types are
 * the contract that feature modules, the persistence port and a future Dockview
 * adapter all program against. A `PanelDescriptor` is plain, serialisable data —
 * never a library object.
 */

/** Presentation family chosen by an explicit, persisted device mode (WS-016). */
export type DeviceMode = "desktop" | "tablet" | "mobile";

/**
 * Logical slot a panel belongs to. Kept an open `string` so the slot catalogue
 * is additive (multi-monitor pop-out slots, WS-007..010, can be introduced
 * without a schema break). The built-ins below are the alpha set.
 */
export type WorkspaceSlot = string;

export const WORKSPACE_SLOTS = ["main", "side", "bottom"] as const;

export interface PanelPosition {
  readonly x: number;
  readonly y: number;
}

export interface PanelSize {
  readonly width: number;
  readonly height: number;
}

/** Min sizes are mandatory; a missing max means "unbounded". */
export interface PanelConstraints {
  readonly minWidth: number;
  readonly minHeight: number;
  readonly maxWidth?: number;
  readonly maxHeight?: number;
}

/**
 * One panel instance in a saved layout. Geometry is meaningful in `desktop`
 * mode; `slot`, `order`, `collapsed` and `fullscreen` also drive the fixed
 * tablet/mobile shells.
 */
export interface PanelDescriptor {
  readonly id: string;
  readonly slot: WorkspaceSlot;
  readonly position: PanelPosition;
  readonly size: PanelSize;
  readonly constraints: PanelConstraints;
  readonly collapsed: boolean;
  readonly fullscreen: boolean;
  /** Stable ordering for slot layout and tablet/mobile stacking. */
  readonly order: number;
}

/**
 * A versioned, adapter-neutral workspace layout. `schemaVersion` is present from
 * the very first commit so an outdated persisted layout can always be migrated
 * or safely rejected (the single biggest legacy gap, W6).
 */
export interface WorkspaceLayout {
  readonly schemaVersion: number;
  readonly presetId: string;
  readonly panels: readonly PanelDescriptor[];
}

/** A named default arrangement for one device mode. */
export interface WorkspacePreset {
  readonly id: string;
  readonly deviceMode: DeviceMode;
  readonly layout: WorkspaceLayout;
}

/** Pixel bounds of the desktop canvas, used to clamp drag/resize. */
export interface CanvasBounds {
  readonly width: number;
  readonly height: number;
}

/**
 * FlexLayout engine additions (ADR-UI-003). These are additive to the
 * ADR-UI-001 types above, not a replacement — both engines' types coexist in
 * this module until Round B cuts the desktop/tablet workspace over.
 */

/**
 * The full set of registrable panel ids, reusing the EXISTING ids already in
 * production use across `operations-route.tsx`/`operations-workspace-presets.ts`
 * (legacy assessment §5: never a speculative id for a module with no
 * backend/UI). Single source of truth for both the `WorkspaceComponentId`
 * type and the `isWorkspaceComponentId` runtime guard in
 * `registry/component-registry.ts`.
 */
export const WORKSPACE_COMPONENT_IDS = [
  // The sequential operating picture: every E3 phase with its live task state,
  // in order, deep-linking into the specialist panels. First tab everywhere.
  "workflow-board",
  "situation-map",
  // Split out of `situation-map` so the Situation workspace can dock the map,
  // the review register and the object editor side by side instead of stacking
  // them into one scrolling panel.
  "situation-objects",
  "situation-object-editor",
  "map-legend",
  "mission-board",
  // Split out of `mission-board`, which carried five functions' work on one
  // panel and was therefore navigable by nobody and portable to no phone
  // screen. Force declaration and movement stay together: movement reports
  // hang off individual declaration rows, so separating them would mean
  // rendering the registration list twice.
  "mission-orders",
  // Redesign migration: full-fidelity legacy fallback for `mission-orders`
  // while the Level 2 workbench is verified — see operations-component-
  // registry.ts and docs/design/redesign-panel-audit.md.
  "legacy-mission-orders",
  "field-tasks",
  "mobilization",
  // Compact role-owned projections over the shared order / mobilization
  // lifecycle. These do not create a second source of operational state.
  "order-execution-status",
  "deployment-asset-status",
  "etb-log",
  "collaboration",
  "personnel-directory",
  "asset-directory",
  "readiness-status",
  "command-structure",
  // Einsatzbeendigung und Einsatzabschluss: the one stage-aware ending
  // workspace. Replaces the three divergent close/reopen surfaces.
  "incident-completion",
  "incident-information",
  "incident-role-assignment",
  "incident-assigned-roles",
  // Consolidated requirement and force-sourcing workbench.
  "deployment-planning",
  // Dispatch-owned commitment and allocation for an approved force package.
  "commitment-allocation",
  "deployment-roster",
  "resource-requests",
  "logistics",
  "incident-reporting",
  "requirement-review",
  // Dedicated LuK Requirements workspace. The legacy `requirement-review`
  // component remains registered for existing Operations layouts and links.
  "operational-requirements",
  "fachberater-submissions",
  "safety-checks",
  "lage-board",
  "reporting",
  "alerting",
  "dispatch-alerting",
  "alert-responses",
] as const;

/**
 * Admin / master-data / bridged-page panel ids (ADR-UI-004). The id manifest is
 * closed but EXTENSIBLE: a new module adds its id here and registers a
 * definition. Keeping one canonical list preserves the security property that
 * only a known id can ever resolve to a component (a typo, `__proto__` or a
 * crafted path is still rejected by `isWorkspaceComponentId`) while removing the
 * old "operations-only" ceiling. `route-*` ids are the generic bridge that hosts
 * an existing routed page as a workspace panel until it is ported natively.
 */
export const ADMIN_WORKSPACE_COMPONENT_IDS = [
  "administration-navigation",
  "administration-context",
  "locations",
  "route-administration",
  "route-mission",
  "route-modules",
  "route-home",
] as const;

/** The single source of truth for both `WorkspaceComponentId` and the runtime guard. */
export const ALL_WORKSPACE_COMPONENT_IDS = [
  ...WORKSPACE_COMPONENT_IDS,
  ...ADMIN_WORKSPACE_COMPONENT_IDS,
] as const;

/**
 * The full set of registrable ids: the closed built-in list above, plus the
 * open `feature-<id>` namespace (ADR-UI-004) used by the generic feature-panel
 * bridge, which hosts any role-assigned shell feature module as a dockable
 * FlexLayout panel. The registry map remains the authoritative whitelist — an
 * unregistered id renders `UnavailableWorkspaceComponent` and never resolves to
 * a component — so widening the type does not widen what can actually mount.
 */
export type WorkspaceComponentId = (typeof ALL_WORKSPACE_COMPONENT_IDS)[number] | `feature-${string}`;

/** Reachability label only (e.g. cross-linking from Administration); it does not migrate Administration onto FlexLayout. */
export type WorkspaceContext = "global" | "mission" | "resource" | "administration";

/** The props every FlexLayout-hosted panel component receives. */
export interface WorkspaceTabProps {
  readonly tabId: string;
  readonly config: unknown;
}

/**
 * A versioned, persisted FlexLayout layout. `model`'s real shape is
 * FlexLayout's own `IJsonModel`; kept `unknown` here until
 * `flexlayout/layout-envelope.ts` validates it, matching the existing
 * `migration.ts` convention of validating `unknown` into a typed shape.
 */
export interface WorkspaceLayoutEnvelope {
  readonly schemaVersion: number;
  readonly presetId: string;
  readonly deviceMode: DeviceMode;
  readonly model: unknown;
}
